
import { useEffect, useState } from "react"
import { Flex, Card, Tag, Popover } from "antd"
import { UpOutlined, DownOutlined, UserOutlined, CalendarOutlined, CheckOutlined, EditOutlined, WechatOutlined, CloseOutlined, UserSwitchOutlined, ClockCircleOutlined, BellOutlined, CommentOutlined, DoubleRightOutlined } from "@ant-design/icons"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { resolveNodeTranslation } from "../../utils/workflowTranslations"
import { spaceColorBgMapping } from "../../utils/colorBgColorMap"
import { getLocalFormattedDate, getLocalFormattedDateWithoutTime, getLocalDateFormatForWorkflow } from "../../utils/getLocalFormattedDate"
import { getStepApprovers } from "../../utils/requestHelper"
import { tagColorMapping } from "../../utils/workflowHelper"
import SvgIcons from "../layout/assets/SvgIcons"

const cardStatusColorMapping = {
    "approved": { "color": "#50A248", "bg": "#EEF6ED" },
    "rejected": { "color": "#F5222D", "bg": "#FFF1F0" },
    "pending": { "color": "#FAAD14", "bg": "#FFFBE6" },
    "submitted": { "color": "#141BCB", "bg": "#E8E8FA" },
    "skipped": { "color": "#b6b5b5", "bg": "#00000005" },
    "closed": { "color": "#e84545", "bg": "#fdf2f2" }
}

const STATUS_I18N_KEYS = {
    approved: "Approved",
    rejected: "Rejected",
    pending: "Pending",
    submitted: "Submitted",
    skipped: "Skipped",
    closed: "Closed",
    refused: "Refused",
    delayed: "Delayed",
}

const WorkflowCard = ({ openDefault = false, isRequestor = false, requestDetail, data, index, currentValues, definedWidgets = [], spaceSpecificUsers = [], spaceSpecificGroups = [], approvedStepLabel = "" }) => {
    const { userInfo, userGroupSpaces = [] } = useSelector(state => state.user)
    const { t, i18n } = useTranslation()
    const translateStatus = (status) => {
        if (!status) return ""
        const key = STATUS_I18N_KEYS[String(status).toLowerCase()]
        return key ? t(key) : status
    }
    // Display the step label in the viewer's language; all status logic below
    // continues to use the canonical `stepData.name`.
    const displayName = resolveNodeTranslation(data, i18n.language)?.name
    const [approvers, setApprovers] = useState([])
    const [notifiedUsers, setNotifiedUsers] = useState([])
    const [collaborators, setCollaborators] = useState([])
    let approvedStatusLabel = requestDetail?.originalWorkflow?.nodes?.find(node => node.data?.type === "end")?.data?.name || ""
    const stepData = data

    const assignee = data?.type === "default" ? data?.actionBy : requestDetail?.createdBy?.name || userInfo.name
    const [isOpen, setIsOpen] = useState(openDefault)
    const bgColor = data?.approvalStatus === "rejected" ? "#FFF1F0" : (stepData?.name === "Approved") ? "#eef6ed" : stepData?.stepColor ? spaceColorBgMapping[stepData.stepColor.toUpperCase()] : "#E7F5FF"
    // const iconColor = data.approvalStatus === "rejected" ? "#F5222D" : stepData?.name === "approved" ? "#50A248" : stepData.stepColor ? stepData.stepColor : "#0A99FE"
    const stepColor = data?.approvalStatus === "rejected" ? "#F5222D" : (stepData?.name === "Approved") ? "#50A248" : stepData?.stepColor ? stepData.stepColor : "#0A99FE"
    const iconColor = "#141BCB";
    const statusColor = data?.approvalStatus ? cardStatusColorMapping[data?.approvalStatus]?.color : iconColor
    const statusBg = data?.approvalStatus ? cardStatusColorMapping[data?.approvalStatus]?.bg : bgColor
    let statuses = data?.statuses || []

    useEffect(() => {
        if (currentValues) {
            let stepApprovers = getStepApprovers(stepData.assignTo, currentValues, definedWidgets, spaceSpecificGroups, true)
            let stepNotifiedUser = getStepApprovers(stepData.notifyTo, currentValues, definedWidgets, spaceSpecificGroups, true)
            setApprovers(stepApprovers)
            setNotifiedUsers(stepNotifiedUser)
            let stepCollaborators = stepData.collaborators?.user || []
            setCollaborators(stepCollaborators)
        }

    }, [currentValues, definedWidgets, spaceSpecificGroups])

    const onOpenToggle = () => {
        setIsOpen(!isOpen)
    }

    return (
        <Card title={
            <Flex className={"card-title cursor-pointer"} justify="space-between" onClick={() => onOpenToggle()}>
                <Flex align="center" gap={8}>
                    <div className="card-label" style={{ background: stepData?.name === approvedStepLabel ? "#eef6ed" : bgColor }}>
                        {
                            stepData?.name === approvedStepLabel ?
                                <CheckOutlined style={{ color: "#50A248" }} />
                                : stepData?.approvalStatus === "approved" ?
                                    <CheckOutlined style={{ color: stepColor }} />
                                    : stepData?.approvalStatus === "rejected" ?
                                        <CloseOutlined style={{ color: stepColor }} />
                                        : stepData?.approvalStep ? <CheckOutlined style={{ color: stepColor }} /> : <EditOutlined style={{ color: stepColor }} />
                        }
                    </div>
                    {stepData?.name === "approved" ? t("Approved") : stepData?.name === "rejected" ? t("Rejected") : displayName}
                </Flex>
                {
                    stepData?.name === approvedStepLabel ? null :
                    stepData?.delayed ? <div className="card-status" style={{ background: "#fff7e6" }}>
                        <ClockCircleOutlined style={{ color: "#faad14" }} />
                        <span className="approval-status" style={{ color: stepColor }}>{t('Delayed')}</span>
                    </div>
                        :
                        (stepData?.approvalStatus || stepData?.name?.toLowerCase() === "approved") ?
                            <div className="card-status" style={{ background: statusBg }}>
                                {stepData.approvalStatus === "rejected" ? <CloseOutlined style={{ color: statusColor }} />
                                    : stepData.approvalStatus === "pending" ? <ClockCircleOutlined style={{ color: statusColor }} />
                                        : stepData.approvalStatus === "skipped" ? <span style={{ color: "#b6b5b5", display: "flex", alignItems: "center" }}><SvgIcons type="skip" /></span>
                                            // <DoubleRightOutlined style={{color: "#b6b5b5"}} />
                                            : <CheckOutlined style={{ color: statusColor }} />}

                                <span className="approval-status" style={{ color: statusColor }}>{stepData?.name === "approved" ? t("Approved") : translateStatus(stepData?.approvalStatus)}</span>
                            </div>
                            : null
                }
            </Flex>
        } extra={
            stepData?.name === approvedStepLabel ? null : (
                isOpen ? (
                    <UpOutlined onClick={() => onOpenToggle()} />
                ) : (
                    <DownOutlined onClick={() => onOpenToggle()} />
                )
            )
        }>
            <div className={`assignee-info ${isOpen ? "" : "collapsed-content"}`}>
                <div className="assignee-title">
                    <UserOutlined style={{ color: stepColor }} />
                    {
                        isRequestor ?
                            <div className="title-label">{assignee}</div>
                            :
                            <>
                                <div className="title-label">{t('Assigned to:')}</div>
                                <Popover
                                    content={(approvers && approvers.length) ? (
                                        <div style={{ padding: "7px 15px" }}>
                                            {
                                                approvers.map((r) => (
                                                    <Flex key={r}
                                                        justify="space-between"
                                                        gap={16}
                                                    >
                                                        {r}
                                                    </Flex>
                                                ))
                                            }
                                        </div>) : null}
                                    placement="right"
                                    trigger="hover"
                                >

                                    <Tag color={tagColorMapping[iconColor]} bordered={false}><span>{approvers.length}</span> <span> {approvers.length > 1 ? t("approvers") : t("approver")}</span></Tag>
                                </Popover>
                            </>
                    }
                </div>
                {
                    !isRequestor ?
                        <div className="assignee-title">
                            <BellOutlined style={{ color: stepColor }} />
                            <div className="title-label">{t('Notify to:')}</div>
                            <Popover
                                content={(notifiedUsers && notifiedUsers.length) ? (
                                    <div style={{ padding: "7px 15px" }}>
                                        {
                                            notifiedUsers.map((r) => (
                                                <Flex key={r}
                                                    justify="space-between"
                                                    gap={16}
                                                >
                                                    {r}
                                                </Flex>
                                            ))
                                        }
                                    </div>) : null}
                                placement="right"
                                trigger="hover"
                            >

                                <Tag color={tagColorMapping[iconColor]} bordered={false}><span>{notifiedUsers.length}</span> <span> {notifiedUsers.length > 1 ? t("notified users") : t("notified user")}</span></Tag>
                            </Popover>
                        </div>
                        : null
                }
                {
                    stepData?.delegatedUser?.length > 0 ?
                        <div className="assignee-title">
                            <UserSwitchOutlined style={{ color: stepColor }} />
                            <div className="title-label">{t('Delegated User:')}</div>
                            <Flex>
                                {
                                    stepData.delegatedUser.map(user => {
                                        return <Tag color={"blue"} bordered={false}>{user}</Tag>
                                    })
                                }
                            </Flex>
                        </div>
                        : null
                }
                {
                    collaborators?.length > 0 ?
                        <div className="assignee-title">
                            <BellOutlined style={{ color: stepColor }} />
                            <div className="title-label">{t('Collaborators:')}</div>
                            <Popover
                                content={(collaborators && collaborators.length) ? (
                                    <div style={{ padding: "7px 15px" }}>
                                        {
                                            collaborators.map((r) => (
                                                <Flex key={r}
                                                    justify="space-between"
                                                    gap={16}
                                                >
                                                    {r}
                                                </Flex>
                                            ))
                                        }
                                    </div>) : null}
                                placement="right"
                                trigger="hover"
                            >

                                <Tag color={tagColorMapping[iconColor]} bordered={false}><span>{collaborators.length}</span> <span> {collaborators.length > 1 ? t("Collaborators") : t("collaborator")}</span></Tag>
                            </Popover>
                        </div>
                        : null
                }
                {
                    !isRequestor && stepData?.deadline && (stepData?.dueDate) &&
                    <div className="assignee-title">
                        <CalendarOutlined style={{ color: stepColor }} />
                        <div className="title-label">{t('Due Date:')}</div>
                        <div className="step-due-date">{getLocalFormattedDateWithoutTime(stepData.dueDate || requestDetail.dueDate)}</div>
                    </div>
                }
                {/* {
                    stepData.delegatedUser && stepData.actionBy ?

                        <div className="assignee-title">
                            <UserOutlined style={{ color: stepColor }} />
                            <div className="title-label"><span className="approval-status">{stepData.approvalStatus}</span> By:</div>
                            <Tag color={"blue"} bordered={false}>{stepData?.actionBy}</Tag>

                        </div>
                        : null
                } */}
                {
                    stepData.actionOn && isRequestor  ?
                        <div className="assignee-title">
                            <CalendarOutlined style={{ color: stepColor }} />
                            <div className="title-label"><span className="approval-status">{translateStatus(stepData.approvalStatus)}</span> {t('On:')}</div>
                            <div className="step-due-date">{getLocalDateFormatForWorkflow(stepData.actionOn)}</div>
                        </div>
                        : null

                }
                {
                    statuses?.length > 0 ?
                        <div className="step-approval-history">
                            <div className="step-due-date">{t('Status:')}</div>
                            {
                                statuses.map(status => {
                                    // Each approver acts at their own time, so the timestamp comes
                                    // from the status entry - never from the step, which holds a
                                    // single `actionOn` for the whole step.
                                    const actionedOn = getLocalFormattedDate(status.updatedOn)
                                    return <div className="approval-history">
                                        <div className="assignee-title">

                                            <div className="title-label" style={{ marginLeft: 33 }}>
                                                <Tag color={status.approvalStatus === "approved" ? "green" : status.approvalStatus === "rejected" ? "red" : tagColorMapping[iconColor]} bordered={false}>{status.actionBy}</Tag>
                                            </div>
                                            {/* <div className="step-due-date">{status.comment}</div> */}
                                        </div>
                                        <div className="assignee-title">
                                            <div style={{ marginLeft: 33, fontSize: 12, color: "#00000073", marginTop: 5, marginBottom: 4 }}>
                                                {actionedOn ? t("{{status}} on {{date}}", { status: translateStatus(status.approvalStatus), date: actionedOn }) : translateStatus(status.approvalStatus)}
                                            </div>
                                        </div>
                                        {
                                            status?.comment ? 
                                                <div style={{ marginLeft: 33, marginBottom: 10, color: "#000000E0", backgroundColor: "#F5F5F5", padding: "7px 10px", borderRadius: 8, fontSize: 12 }}>
                                                    <CommentOutlined style={{ fontSize: 15, color: "#00000073", position: "relative", top: 2, marginRight: 6 }} />
                                                    {status.comment}
                                                </div>
                                            : null
                                        }
                                    </div>
                                })
                            }
                        </div>
                        : null
                }

            </div>
        </Card>
    )
}

export default WorkflowCard